import React, { useState, useEffect } from "react";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@mmfestival/shared-components";
import { db, storage } from "@lib/firebase";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  updateProfile,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { InputFieldProfile, InputField, Button } from "@mmfestival/shared-components";
import { authPost } from "../lib/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserEdit,
  faCamera,
  faLock,
  faSignOutAlt,
} from "@fortawesome/free-solid-svg-icons";

export const Profile = ({ labelColor = "text-slate-700" }) => {
  const { user, logout } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    bibName: "",
    email: "",
    telepon: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    golonganDarah: "",
    sizeJersey: "",
    gender: "",
    negara: "",
    provinsi: "",
    kota: "",
    alamat: "",
    kontakDarurat: "",
    namaKontakDarurat: "",
    hubunganKontakDarurat: "",
  });
  const [loading, setLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState(() => {
    const stored = localStorage.getItem("activeMenu");
    const valid = ["Edit Profile", "Change Profile Photo", "Change Password"];
    return valid.includes(stored) ? stored : "Edit Profile";
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    localStorage.setItem("activeMenu", activeMenu);
  }, [activeMenu]);

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        if (!user) {
          return;
        }

        if (!user.emailVerified) {
          await logout();
          if (isCancelled) return;
          navigate("/login");
          return;
        }

        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);
        if (isCancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          const fullName = data.namaLengkap || user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          if (!isCancelled) {
            setFormData({
              ...data,
              namaDepan,
              namaBelakang: namaBelakang.join(" "),
            });
          }
        } else {
          const fullName = user.displayName || "";
          const [namaDepan, ...namaBelakang] = fullName.split(" ");
          if (!isCancelled) {
            setFormData({
              namaDepan,
              namaBelakang: namaBelakang.join(" "),
              email: user.email,
              bibName: "",
              telepon: "",
              nomorIdentitas: "",
              tanggalLahir: "",
              golonganDarah: "",
              sizeJersey: "",
              gender: "",
              negara: "",
              provinsi: "",
              kota: "",
              alamat: "",
              kontakDarurat: "",
              namaKontakDarurat: "",
              hubunganKontakDarurat: "",
            });
          }
          console.log("User data not found in Firestore, using displayName");
        }

      } catch (err) {
        console.error("Failed to fetch profile data:", err);
        if (isCancelled) return;

        const isPermissionError =
          err.code === "permission-denied" ||
          (err.message &&
            err.message.includes("Missing or insufficient permissions"));

        if (isPermissionError) {
          // Permission errors are likely due to auth state change or security rules.
          // Avoid showing noisy modal; 
          console.warn(
            "Permission error while fetching profile."
          );
          // Do NOT logout automatically. This causes login loops if the backend rules are temporarily strict.
          // try {
          //   await logout();
          // } catch (e) {
          //   console.warn("Logout failed while handling permission error", e);
          // }
          // if (!isCancelled) navigate("/login");
          return;
        }

        // Only show notification if user still present and this is a genuine failure
        if (user) {
          showNotification("error", "Failed to fetch user data.");
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [user, logout, navigate, showNotification]);

  // ---------- Profile submit / update ----------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fullName =
        `${formData.namaDepan || ""} ${formData.namaBelakang || ""}`.trim() ||
        user.displayName ||
        user.email ||
        "";

      const profileData = {
        namaDepan: formData.namaDepan || "",
        namaBelakang: formData.namaBelakang || "",
        namaLengkap: fullName,
        bibName: formData.bibName || "",
        email: user.email || formData.email || "",
        telepon: formData.telepon || "",
        nomorIdentitas: formData.nomorIdentitas || "",
        tanggalLahir: formData.tanggalLahir || "",
        golonganDarah: formData.golonganDarah || "",
        sizeJersey: formData.sizeJersey || "",
        gender: formData.gender || "",
        negara: formData.negara || "",
        provinsi: formData.provinsi || "",
        kota: formData.kota || "",
        alamat: formData.alamat || "",
        kontakDarurat: formData.kontakDarurat || "",
        namaKontakDarurat: formData.namaKontakDarurat || "",
        hubunganKontakDarurat: formData.hubunganKontakDarurat || "",
      };

      await authPost("/api/profile/sync", profileData);

      showNotification("success", "Profile updated successfully!");
    } catch (err) {
      console.error("Failed to save data:", err);
      // Tampilkan alasan dari server (mis. tanggal lahir tidak memenuhi syarat
      // usia kategori) supaya peserta tahu apa yang harus diperbaiki.
      const serverMessage = err?.response?.data?.message;
      showNotification(
        "error",
        serverMessage || "An error occurred while saving data!"
      );
    } finally {
      setLoading(false);
    }
  };

  // ---------- Photo upload ----------
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showNotification("error", "File must be an image (jpg, png, etc.)!");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification("error", "Maximum image size is 5MB!");
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoSubmit = async (e) => {
    e.preventDefault();
    if (!photoFile) {
      showNotification("error", "Please choose an image first!");
      return;
    }

    setLoading(true);
    try {
      const storageRef = ref(storage, `profilePictures/${user.uid}`);
      await uploadBytes(storageRef, photoFile);
      const photoURL = await getDownloadURL(storageRef);

      await updateProfile(user, { photoURL });

      await updateDoc(doc(db, "users", user.uid), { photoURL });

      showNotification("success", "Profile photo updated successfully!");
      setPhotoFile(null);
      setPhotoPreview(null);
    } catch (err) {
      console.error("Failed to upload profile photo:", err);
      showNotification(
        "error",
        "Failed to upload profile photo. Please try again!"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoCancel = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // ---------- Password change ----------
  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!passwordData.currentPassword) {
        showNotification("error", "Please enter your current password!");
        setLoading(false);
        return;
      }
      if (passwordData.newPassword.length < 6) {
        showNotification(
          "error",
          "New password must be at least 6 characters!"
        );
        setLoading(false);
        return;
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        showNotification(
          "error",
          "New password and confirmation do not match!"
        );
        setLoading(false);
        return;
      }

      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(user, credential);

      await updatePassword(user, passwordData.newPassword);

      await updateDoc(doc(db, "users", user.uid), {
        lastPasswordChange: serverTimestamp(),
      });

      showNotification("success", "Password updated successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      console.error("Failed to update password:", err);
      if (err.code === "auth/wrong-password") {
        showNotification("error", "Current password is incorrect!");
      } else if (err.code === "auth/too-many-requests") {
        showNotification("error", "Too many attempts. Please try again later!");
      } else {
        showNotification(
          "error",
          "Failed to update password. Please try again!"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
  };


  const isGoogleLogin = user?.providerData.some(
    (provider) => provider.providerId === "google.com"
  );

  // MENU DEFINITION
  const menus = [
    { name: "Edit Profile", icon: faUserEdit },
    { name: "Change Profile Photo", icon: faCamera },
    { name: "Change Password", icon: faLock },
    { name: "Logout", icon: faSignOutAlt, isLogout: true },
  ];

  const renderContent = () => {
    switch (activeMenu) {
      case "Edit Profile":
        return (
          <div className="bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4">
            <h2 className="text-2xl font-black text-slate-700 mb-6">
              Edit Profile
            </h2>
            <div className="flex justify-center mb-6">
              <img
                src={
                  user?.photoURL ||
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
                }
                alt="Avatar"
                className="w-24 h-24 rounded-full border-4 border-gray-100 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                }}
              />
            </div>
            <p className="text-lg mb-6 text-center text-slate-700">
              Hello,{" "}
              <strong>
                {`${formData.namaDepan} ${formData.namaBelakang}` ||
                  user?.displayName ||
                  user.email}
              </strong>
            </p>
            <form onSubmit={handleSubmit}>
              <InputFieldProfile
                formData={formData}
                handleChange={handleChange}
                isFormDisabled={loading}
                labelColor={labelColor}
              />
              <div className="flex justify-center mt-6">
                <Button
                  type="submit"
                  disabled={loading}
                  className="max-w-xs"
                >
                  {loading ? "Saving..." : "Save Data"}
                </Button>
              </div>
            </form>
          </div>
        );
      case "Change Profile Photo":
        return (
          <div className="bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4">
            <h2 className="text-2xl font-black text-slate-700 mb-6">
              Change Profile Photo
            </h2>
            <div className="flex justify-center mb-6">
              <img
                src={
                  photoPreview ||
                  user?.photoURL ||
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y"
                }
                alt="Avatar Preview"
                className="w-32 h-32 rounded-full border-4 border-gray-100 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
                }}
              />
            </div>
            <form onSubmit={handlePhotoSubmit}>
              <InputField
                label="Choose Image"
                name="photo"
                type="file"
                onChange={handlePhotoChange}
                accept="image/*"
                disabled={loading}
                labelColor={labelColor}
              />
              <div className="flex justify-center gap-4 mt-4">
                <Button
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Uploading..." : "Upload Photo"}
                </Button>
                {photoFile && (
                  <button
                    type="button"
                    onClick={handlePhotoCancel}
                    className="px-4 py-2 bg-slate-50 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        );
      case "Change Password":
        return (
          <div className="bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4">
            <h2 className="text-2xl font-black text-slate-700 mb-6">
              Change Password
            </h2>
            {isGoogleLogin ? (
              <p className="text-slate-700">
                Your account uses Google login. Passwords are managed by Google.
              </p>
            ) : (
              <form onSubmit={handlePasswordSubmit}>
                <InputField
                  label="Current Password"
                  name="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="New Password"
                  name="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <InputField
                  label="Confirm Password"
                  name="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  required
                  disabled={loading}
                  labelColor={labelColor}
                />
                <div className="flex justify-center gap-4 mt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-800 transition-colors duration-200"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={handlePasswordCancel}
                    className="px-6 py-2 bg-slate-50 text-slate-700 rounded-xl hover:bg-slate-100 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const handleLogout = async () => {
    showConfirm(
      "Are you sure you want to log out?",
      async () => {
        setLoading(true);
        try {
          await logout();
          navigate("/login");
        } catch (err) {
          console.error("Failed to logout:", err);
          showNotification("error", "Failed to logout. Please try again!");
        } finally {
          setLoading(false);
        }
      },
      "Yes, Log out",
      "Cancel"
    );
  };

  const handleMenuClick = (menu) => {
    if (menu.isLogout) {
      handleLogout();
    } else {
      setActiveMenu(menu.name);
    }
  };

  return (
    <section className="w-full min-h-screen py-20 bg-white text-slate-700">
      <div className="container mx-auto px-4 md:px-4 max-w-6xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-64 bg-white rounded-xl md:border md:border-slate-200 px-0 md:px-4 py-4">
            <ul>
              {menus.map((menu) => (
                <li key={menu.name}>
                  <button
                    onClick={() => handleMenuClick(menu)}
                    className={`w-full flex items-center gap-2 px-4 py-2 mb-2 text-md font-medium rounded-xl transition-colors duration-200 ${
                      menu.isLogout
                        ? "text-red-600 hover:bg-red-50"
                        : activeMenu === menu.name
                        ? "bg-slate-700 text-white"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={menu.icon} />
                    {menu.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex-1">{renderContent()}</div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
